/**
 * GroupFrame — R232q22 Phase 5 (Sirus 1:1 UI rebuild).
 *
 * Party-frame stack rendered directly under PlayerFrame (top:90, left:4).
 * Mirrors the WoW 3.3.5a / Sirus party panel: up to 5 vertically stacked
 * mini-frames, each with a tiny race-tinted portrait, HP + power bars,
 * 12px Cinzel name, level pill, class glyph, and an optional leader crown
 * for the party leader.
 *
 * Data source:
 *   – `useNet().groupMembers` — array of mini-rows (id/name/level/classId/
 *     raceId/hp/hpMax/resource/resourceMax + optional `leader`/`offline`
 *     flags). When the field is missing on the store (Phase 5 ships before
 *     the Colyseus group sync), we fall back to an empty list and the
 *     frame renders nothing.
 *
 * Interactions:
 *   – left click  → useTarget.setTarget(id)
 *   – right click → custom context menu: «Сделать целью», «Передать
 *                   лидерство», «Удалить из группы», «Покинуть группу».
 *                   The handlers call into `useTarget` for the obvious
 *                   verb and emit `console.info` placeholders for the
 *                   leader / kick / leave actions until the Colyseus
 *                   `RoomCommands` channel is wired up.
 *   – hover       → tooltip via `UnitTooltip` (mounted inline so the row
 *                   anchor rect is correct).
 *
 * Layout: position fixed (top:90 left:4) so the stack hangs immediately
 * under the 76-tall PlayerFrame at top:4. z-index 30 — same layer as the
 * other always-on HUD chrome. On a coarse pointer (`@media (pointer:
 * coarse)`) the panel re-anchors to the right edge of the viewport so it
 * doesn't fight the action bars on touch.
 *
 * Hidden when the group is empty (length 0) — returns null.
 */

import { useCallback, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { useNet } from '../../state/net-store.js';
import { useTarget } from '../../state/target-store.js';
import { RACES, type RaceId } from '../../data/wow-races.js';
import { classById, type ClassId } from '../../data/classes.js';
import { UnitTooltip } from './UnitTooltip.js';

/** Class → resource bar key. Matches PlayerFrame.tsx convention. */
type ResourceKey = 'mana' | 'rage' | 'focus' | 'energy' | 'runic' | 'faith';

const RESOURCE_TOKEN: Record<ResourceKey, string> = {
  mana: 'var(--aiwari-bar-mana)',
  rage: 'var(--aiwari-bar-rage)',
  focus: 'var(--aiwari-bar-focus)',
  energy: 'var(--aiwari-bar-energy)',
  runic: 'var(--aiwari-bar-runic)',
  faith: 'var(--aiwari-bar-mana)',
};

/** Minimal shape every consumer of the group panel cares about. */
export interface GroupMember {
  id: string;
  name: string;
  level: number;
  classId: ClassId | string | null;
  raceId: RaceId | string | null;
  hp: number;
  hpMax: number;
  resource: number;
  resourceMax: number;
  /** Set true on the party leader; renders the gold crown overlay. */
  leader?: boolean;
  /** Greys out the row when true (offline / dropped connection). */
  offline?: boolean;
}

// R232q86c: stable empty-array sentinel. Returning `[]` from a zustand
// selector creates a fresh reference each call, which makes
// useSyncExternalStore think the snapshot changed every render →
// infinite re-render → React #185 «Maximum update depth exceeded».
// Holding one frozen array keeps the snapshot identity stable until the
// real `groupMembers` field lands on net-store.
const EMPTY_GROUP_MEMBERS: readonly GroupMember[] = Object.freeze([]);

/** Read groupMembers off the net-store without forcing the type yet. */
function useGroupMembers(): readonly GroupMember[] {
  return useNet((s) => {
    const raw = (s as unknown as { groupMembers?: unknown }).groupMembers;
    if (!Array.isArray(raw)) return EMPTY_GROUP_MEMBERS;
    return raw as GroupMember[];
  });
}

function getRaceTint(raceId: RaceId | string | null | undefined): string {
  if (!raceId) return '#5C4724';
  const found = RACES.find((r) => r.id === raceId);
  return found?.colour ?? '#5C4724';
}

function getResourceFor(classId: ClassId | string | null | undefined): ResourceKey {
  if (!classId) return 'mana';
  const cls = classById(classId as ClassId);
  if (!cls) return 'mana';
  return cls.resource as ResourceKey;
}

/** Tiny crown SVG, identical to PlayerFrame's so the silhouette matches. */
function LeaderCrown() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" aria-hidden style={{ display: 'block' }}>
      <path
        d="M3 18 L4 8 L9 13 L12 6 L15 13 L20 8 L21 18 Z"
        fill="var(--aiwari-gold)"
        stroke="var(--aiwari-gold-dark)"
        strokeWidth="1"
      />
    </svg>
  );
}

interface ContextMenuState {
  x: number;
  y: number;
  member: GroupMember;
}

interface ContextMenuProps {
  state: ContextMenuState;
  onClose: () => void;
  onSetTarget: (id: string) => void;
  onPromoteLeader: (id: string) => void;
  onKick: (id: string) => void;
  onLeave: () => void;
}

/** Tiny inline menu — Russian only, matches Sirus naming. */
function ContextMenu({ state, onClose, onSetTarget, onPromoteLeader, onKick, onLeave }: ContextMenuProps) {
  const items: Array<{ label: string; action: () => void }> = [
    { label: 'Сделать целью', action: () => onSetTarget(state.member.id) },
    { label: 'Передать лидерство', action: () => onPromoteLeader(state.member.id) },
    { label: 'Удалить из группы', action: () => onKick(state.member.id) },
    { label: 'Покинуть группу', action: () => onLeave() },
  ];
  return (
    <div
      role="menu"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: state.y,
        left: state.x,
        zIndex: 55,
        minWidth: 168,
        padding: 4,
        background: 'var(--aiwari-bg-card-solid)',
        border: '1px solid var(--aiwari-frame-edge)',
        borderRadius: 3,
        boxShadow: '0 4px 14px rgba(0,0,0,0.6)',
        fontFamily: 'var(--aiwari-font-body)',
        fontSize: 12,
        color: 'var(--aiwari-fg)',
      }}
    >
      {items.map((it) => (
        <button
          key={it.label}
          type="button"
          role="menuitem"
          onClick={() => {
            it.action();
            onClose();
          }}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            padding: '4px 8px',
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            font: 'inherit',
            cursor: 'pointer',
            borderRadius: 2,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'hsl(36 25% 18%)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

interface GroupRowProps {
  member: GroupMember;
  onClick: (id: string) => void;
  onContext: (e: MouseEvent<HTMLDivElement>, m: GroupMember) => void;
}

function GroupRow({ member, onClick, onContext }: GroupRowProps) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState(false);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  const tint = getRaceTint(member.raceId);
  const resourceKey = getResourceFor(member.classId);
  const cls = member.classId ? classById(member.classId as ClassId) : undefined;
  const glyph = cls?.name.ru.charAt(0) ?? '?';
  const classColour = cls?.colour ?? 'var(--aiwari-gold)';

  const hpPct = member.hpMax > 0 ? Math.max(0, Math.min(100, (member.hp / member.hpMax) * 100)) : 0;
  const resPct = member.resourceMax > 0 ? Math.max(0, Math.min(100, (member.resource / member.resourceMax) * 100)) : 0;

  const opacity = member.offline ? 0.5 : 1;

  return (
    <>
      <div
        ref={rowRef}
        role="button"
        tabIndex={0}
        aria-label={`Член группы: ${member.name}`}
        onClick={() => onClick(member.id)}
        onContextMenu={(e) => onContext(e, member)}
        onMouseEnter={() => {
          setHover(true);
          setAnchor(rowRef.current?.getBoundingClientRect() ?? null);
        }}
        onMouseLeave={() => setHover(false)}
        style={{
          position: 'relative',
          width: 100,
          height: 40,
          padding: 4,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, hsl(36 25% 12%) 0%, hsl(36 25% 7%) 100%)',
          border: '1px solid var(--aiwari-gold-dark)',
          borderRadius: 3,
          borderLeft: '2px solid hsl(120 60% 40%)',
          opacity,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {/* Portrait 28×28 race-tinted (rubber: clamps via --aiwari-slot-mini). */}
        <div
          aria-hidden
          style={{
            position: 'relative',
            width: 'var(--aiwari-slot-mini)',
            height: 'var(--aiwari-slot-mini)',
            flex: '0 0 var(--aiwari-slot-mini)',
            borderRadius: 2,
            border: '1px solid var(--aiwari-gold-dark)',
            background: `radial-gradient(circle at 30% 30%, ${tint} 0%, hsl(36 25% 5%) 100%)`,
          }}
        >
          {/* Class glyph bottom-right of portrait */}
          <span
            style={{
              position: 'absolute',
              right: -3,
              bottom: -3,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: classColour,
              border: '1px solid var(--aiwari-frame-edge)',
              fontFamily: 'var(--aiwari-font-display)',
              fontSize: 7,
              fontWeight: 700,
              color: '#0F0900',
              lineHeight: '8px',
              textAlign: 'center',
              display: 'inline-block',
            }}
          >
            {glyph}
          </span>
          {/* Level pill top-right of portrait */}
          <span
            style={{
              position: 'absolute',
              right: -3,
              top: -3,
              minWidth: 12,
              height: 12,
              padding: '0 2px',
              borderRadius: 6,
              background: 'var(--aiwari-gold)',
              border: '1px solid var(--aiwari-gold-dark)',
              fontFamily: 'var(--aiwari-font-mono)',
              fontSize: 8,
              fontWeight: 700,
              color: '#0F0900',
              lineHeight: '10px',
              textAlign: 'center',
            }}
          >
            {member.level}
          </span>
          {/* Leader crown overlay */}
          {member.leader ? (
            <span style={{ position: 'absolute', left: -4, top: -6 }}>
              <LeaderCrown />
            </span>
          ) : null}
        </div>

        {/* Right: name on top, HP bar, resource bar */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              fontFamily: 'var(--aiwari-font-display)',
              fontSize: 'var(--aiwari-text-sm)',
              lineHeight: 1,
              color: 'var(--aiwari-fg)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={member.name}
          >
            {member.name}
          </span>
          {/* HP bar — height flows from --aiwari-bar-h. */}
          <div className="aiwari-bar" style={{ height: 'var(--aiwari-bar-h)', width: 64 }} aria-label={`HP ${Math.round(member.hp)}/${Math.round(member.hpMax)}`}>
            <div
              className="aiwari-bar-fill"
              style={{ width: `${hpPct}%`, background: 'var(--aiwari-bar-hp)' }}
            />
          </div>
          {/* Resource bar — thin sliver via --aiwari-bar-h-thin. */}
          <div className="aiwari-bar" style={{ height: 'var(--aiwari-bar-h-thin)', width: 64 }} aria-label={`Ресурс ${Math.round(member.resource)}/${Math.round(member.resourceMax)}`}>
            <div
              className="aiwari-bar-fill"
              style={{ width: `${resPct}%`, background: RESOURCE_TOKEN[resourceKey] }}
            />
          </div>
        </div>
      </div>
      <UnitTooltip unitId={member.id} show={hover} anchorRect={anchor} />
    </>
  );
}

export function GroupFrame() {
  const members = useGroupMembers();
  const setTarget = useTarget((s) => s.setTarget);
  const [menu, setMenu] = useState<ContextMenuState | null>(null);

  const onRowClick = useCallback(
    (id: string) => {
      setTarget(id);
    },
    [setTarget],
  );

  const onRowContext = useCallback((e: MouseEvent<HTMLDivElement>, member: GroupMember) => {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, member });
  }, []);

  const onSetTarget = useCallback(
    (id: string) => {
      setTarget(id);
    },
    [setTarget],
  );

  const onPromoteLeader = useCallback((id: string) => {
    // TODO: send 'group:promote-leader' over Colyseus once the channel ships.
    console.info('[GroupFrame] promote-leader', id);
  }, []);

  const onKick = useCallback((id: string) => {
    console.info('[GroupFrame] kick', id);
  }, []);

  const onLeave = useCallback(() => {
    console.info('[GroupFrame] leave-group');
  }, []);

  // Limit to 5 to match WoW party cap.
  const visible = useMemo(() => members.slice(0, 5), [members]);

  if (visible.length === 0) return null;

  const wrapStyle: CSSProperties = {
    position: 'fixed',
    top: 90,
    left: 4,
    zIndex: 30,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  };

  return (
    <>
      <div data-aiwari-group-frame style={wrapStyle} onClick={() => setMenu(null)}>
        <style>{`
          @media (pointer: coarse) {
            [data-aiwari-group-frame] {
              top: 90px !important;
              left: auto !important;
              right: 4px !important;
            }
          }
        `}</style>
        {visible.map((m) => (
          <GroupRow key={m.id} member={m} onClick={onRowClick} onContext={onRowContext} />
        ))}
      </div>
      {menu ? (
        <ContextMenu
          state={menu}
          onClose={() => setMenu(null)}
          onSetTarget={onSetTarget}
          onPromoteLeader={onPromoteLeader}
          onKick={onKick}
          onLeave={onLeave}
        />
      ) : null}
    </>
  );
}
